import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isFollowingAuthorityCommandsFeatureEnabled,
  isFollowingAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const TARGET_TYPES = new Set(["account", "organisation"]);
const FOLLOWING_LIST_PAGE_SIZE = 50;
const CURSOR_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const COMMAND_RESPONSE_KEYS = Object.freeze([
  "schemaVersion",
  "outcome",
  "following",
  "revision",
  "target",
]);
const READ_RESPONSE_KEYS = Object.freeze([
  "schemaVersion",
  "following",
  "revision",
  "target",
]);
const LIST_RESPONSE_KEYS = Object.freeze([
  "schemaVersion",
  "entries",
  "nextCursor",
  "hasMore",
]);
const LIST_ENTRY_KEYS = Object.freeze(["revision", "target"]);
const MINIMAL_TARGET_KEYS = Object.freeze(["type", "publicIdentifier"]);
const ACCOUNT_TARGET_KEYS = Object.freeze([
  "type",
  "publicIdentifier",
  "displayName",
]);
const ORGANISATION_TARGET_KEYS = Object.freeze([
  "type",
  "publicIdentifier",
  "name",
]);

export class FollowingAuthorityServiceError extends Error {
  constructor(code = "unknown", message = "Following is unavailable.") {
    super(message);
    this.name = "FollowingAuthorityServiceError";
    this.code = code;
  }
}

function fail(code, message = "Following is unavailable.") {
  throw new FollowingAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actual = ownKeys
    .filter((key) => typeof key === "string")
    .sort();
  const expected = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value"),
    ) &&
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function requireExactKeys(value, expectedKeys) {
  if (!hasExactKeys(value, expectedKeys)) {
    fail("invalid-argument", "The Following request is invalid.");
  }
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The Following operation is invalid.");
  }
  return value;
}

function normalizeRevision(value) {
  if (!Number.isSafeInteger(value) || value < 0) {
    fail("invalid-argument", "The Following revision is invalid.");
  }
  return value;
}

function normalizePublicIdentifier(value, code = "invalid-argument") {
  if (typeof value !== "string" || !PUBLIC_IDENTIFIER_PATTERN.test(value)) {
    fail(
      code,
      code === "invalid-response"
        ? "The Following response was invalid."
        : "The Following target is unavailable.",
    );
  }
  return value;
}

function normalizeTarget(value) {
  requireExactKeys(value, ["type", "publicIdentifier"]);
  if (!TARGET_TYPES.has(value.type)) {
    fail("invalid-argument", "The Following target is invalid.");
  }
  return Object.freeze({
    type: value.type,
    publicIdentifier: normalizePublicIdentifier(value.publicIdentifier),
  });
}

export function normalizeGetOwnFollowingTarget(value) {
  return normalizeTarget(value);
}

export function normalizeSetOwnFollowingIntent(value) {
  const intent = requireExactKeys(value, [
    "operationId",
    "expectedRevision",
    "target",
    "following",
  ]);
  if (typeof intent.following !== "boolean") {
    fail("invalid-argument", "The Following state is invalid.");
  }
  return Object.freeze({
    schemaVersion: 1,
    operationId: normalizeOperationId(intent.operationId),
    expectedRevision: normalizeRevision(intent.expectedRevision),
    target: normalizeTarget(intent.target),
    following: intent.following,
  });
}

export function normalizeListOwnFollowingOptions(value) {
  const options = requireExactKeys(value, ["targetType", "cursor", "limit"]);
  if (!TARGET_TYPES.has(options.targetType)) {
    fail("invalid-argument", "The Following target is invalid.");
  }
  if (
    options.limit !== FOLLOWING_LIST_PAGE_SIZE ||
    (options.cursor !== null &&
      (typeof options.cursor !== "string" ||
        options.cursor.length < 32 ||
        options.cursor.length > 2048 ||
        !CURSOR_PATTERN.test(options.cursor)))
  ) {
    fail("invalid-argument", "The Following page is invalid.");
  }
  return Object.freeze({
    schemaVersion: 1,
    targetType: options.targetType,
    cursor: options.cursor,
    limit: FOLLOWING_LIST_PAGE_SIZE,
  });
}

function normalizeText(value) {
  if (
    typeof value !== "string" ||
    value.length < 1 ||
    value.length > 160 ||
    /[\u0000-\u001f\u007f]/.test(value)
  ) {
    fail("invalid-response", "The Following response was invalid.");
  }
  return value;
}

function normalizeResponseTarget(
  value,
  expectedTarget,
  { allowMinimal = false } = {},
) {
  const keys = expectedTarget.type === "account"
    ? ACCOUNT_TARGET_KEYS
    : ORGANISATION_TARGET_KEYS;
  const isMinimal = allowMinimal && hasExactKeys(value, MINIMAL_TARGET_KEYS);
  if (!isMinimal && !hasExactKeys(value, keys)) {
    fail("invalid-response", "The Following response was invalid.");
  }
  const publicIdentifier = normalizePublicIdentifier(
    value.publicIdentifier,
    "invalid-response",
  );
  if (
    value.type !== expectedTarget.type ||
    publicIdentifier !== expectedTarget.publicIdentifier
  ) {
    fail("invalid-response", "The Following response was invalid.");
  }
  if (isMinimal) {
    return Object.freeze({ type: value.type, publicIdentifier });
  }
  if (value.type === "account") {
    return Object.freeze({
      type: value.type,
      publicIdentifier,
      displayName: normalizeText(value.displayName),
    });
  }
  return Object.freeze({
    type: value.type,
    publicIdentifier,
    name: normalizeText(value.name),
  });
}

function validateResponse(value, request) {
  if (
    !hasExactKeys(value, COMMAND_RESPONSE_KEYS) ||
    value.schemaVersion !== 1 ||
    !["followed", "unfollowed", "unchanged"].includes(value.outcome) ||
    typeof value.following !== "boolean" ||
    value.following !== request.following ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    (value.following && value.revision < 1) ||
    (value.outcome === "followed" && !value.following) ||
    (value.outcome === "unfollowed" && value.following)
  ) {
    fail("invalid-response", "The Following response was invalid.");
  }
  return Object.freeze({
    schemaVersion: 1,
    outcome: value.outcome,
    following: value.following,
    revision: value.revision,
    target: normalizeResponseTarget(value.target, request.target, {
      allowMinimal: request.following === false,
    }),
  });
}

function validateListResponse(value, request) {
  if (
    !hasExactKeys(value, LIST_RESPONSE_KEYS) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.entries) ||
    value.entries.length > FOLLOWING_LIST_PAGE_SIZE ||
    typeof value.hasMore !== "boolean" ||
    value.hasMore !== (value.nextCursor !== null) ||
    (value.nextCursor !== null &&
      (typeof value.nextCursor !== "string" ||
        value.nextCursor.length < 32 ||
        value.nextCursor.length > 2048 ||
        !CURSOR_PATTERN.test(value.nextCursor))) ||
    (request.cursor !== null && value.nextCursor === request.cursor)
  ) {
    fail("invalid-response", "The Following response was invalid.");
  }
  const seen = new Set();
  const entries = value.entries.map((entry) => {
    if (
      !hasExactKeys(entry, LIST_ENTRY_KEYS) ||
      !Number.isSafeInteger(entry.revision) ||
      entry.revision < 1 ||
      !isPlainRecord(entry.target) ||
      !TARGET_TYPES.has(entry.target.type)
    ) {
      fail("invalid-response", "The Following response was invalid.");
    }
    const expectedTarget = {
      type: entry.target.type,
      publicIdentifier: normalizePublicIdentifier(
        entry.target.publicIdentifier,
        "invalid-response",
      ),
    };
    if (expectedTarget.type !== request.targetType) {
      fail("invalid-response", "The Following response was invalid.");
    }
    const target = normalizeResponseTarget(entry.target, expectedTarget, {
      allowMinimal: true,
    });
    const key = `${target.type}:${target.publicIdentifier}`;
    if (seen.has(key)) {
      fail("invalid-response", "The Following response was invalid.");
    }
    seen.add(key);
    return Object.freeze({ revision: entry.revision, target });
  });
  return Object.freeze({
    schemaVersion: 1,
    entries: Object.freeze(entries),
    nextCursor: value.nextCursor,
    hasMore: value.hasMore,
  });
}

function validateReadResponse(value, request) {
  if (
    !hasExactKeys(value, READ_RESPONSE_KEYS) ||
    value.schemaVersion !== 1 ||
    typeof value.following !== "boolean" ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    (value.following && value.revision < 1)
  ) {
    fail("invalid-response", "The Following response was invalid.");
  }
  return Object.freeze({
    schemaVersion: 1,
    following: value.following,
    revision: value.revision,
    target: normalizeResponseTarget(value.target, request.target),
  });
}

function normalizeServiceError(error) {
  if (error instanceof FollowingAuthorityServiceError) return error;
  const messages = {
    aborted: "This Follow changed elsewhere. Refresh and try again.",
    "deadline-exceeded": "Following did not respond in time.",
    "failed-precondition": "Following is temporarily unavailable.",
    "invalid-argument": "The Following request was invalid.",
    "not-found": "This Follow target is unavailable.",
    "permission-denied": "This Follow action is not available.",
    "resource-exhausted": "The Following limit was reached.",
    "ui-deadline": "Following did not respond in time.",
    unauthenticated: "Sign in again to manage Following.",
    unavailable: "Following is temporarily unavailable.",
  };
  const candidateCode = String(error?.code || "unknown").split("/").pop();
  const code = Object.hasOwn(messages, candidateCode)
    ? candidateCode
    : "unknown";
  return new FollowingAuthorityServiceError(
    code,
    messages[code] || "The Following operation could not be completed.",
  );
}

function authenticatedActor(authInstance, expectedActorUid) {
  const actor = authInstance?.currentUser;
  const uid = actor?.uid;
  if (
    typeof uid !== "string" ||
    uid.length < 1 ||
    uid.length > 128 ||
    (expectedActorUid !== undefined && uid !== expectedActorUid)
  ) {
    fail("unauthenticated", "Sign in again to manage Following.");
  }
  return Object.freeze({ actor, uid });
}

function requireCurrentActor(authInstance, expected) {
  if (
    authInstance?.currentUser !== expected.actor ||
    authInstance?.currentUser?.uid !== expected.uid
  ) {
    fail(
      "stale-auth-context",
      "The signed-in account changed during the Following operation.",
    );
  }
}

export function createFollowingAuthorityOperationId(
  cryptoProvider = globalThis.crypto,
) {
  if (typeof cryptoProvider?.randomUUID === "function") {
    return `following_${cryptoProvider.randomUUID().replaceAll("-", "_")}`;
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `following_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  fail("failed-precondition", "Secure Following operations are unavailable.");
}

export function createFollowingAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  readsEnabled = isFollowingAuthorityReadsFeatureEnabled,
  commandsEnabled = isFollowingAuthorityCommandsFeatureEnabled,
} = {}) {
  let getCallable;
  let listCallable;
  let setCallable;

  return Object.freeze({
    async getOwnFollowing(targetValue, { expectedActorUid } = {}) {
      if (!readsEnabled) {
        fail("feature-disabled", "Following reads are not enabled.");
      }
      const request = Object.freeze({
        schemaVersion: 1,
        target: normalizeGetOwnFollowingTarget(targetValue),
      });
      const expected = authenticatedActor(authInstance, expectedActorUid);
      if (!getCallable) {
        getCallable = callableFactory(functionsInstance, "getOwnFollowing");
      }
      try {
        const response = await withUiDeadline(getCallable(request), {
          phase: "loading Following",
        });
        requireCurrentActor(authInstance, expected);
        return validateReadResponse(response?.data, request);
      } catch (error) {
        requireCurrentActor(authInstance, expected);
        throw normalizeServiceError(error);
      }
    },
    async listOwnFollowing(
      { targetType, cursor = null, limit = FOLLOWING_LIST_PAGE_SIZE } = {},
      { expectedActorUid } = {},
    ) {
      if (!readsEnabled) {
        fail("feature-disabled", "Following reads are not enabled.");
      }
      const request = normalizeListOwnFollowingOptions({
        targetType,
        cursor,
        limit,
      });
      const expected = authenticatedActor(authInstance, expectedActorUid);
      if (!listCallable) {
        listCallable = callableFactory(functionsInstance, "listOwnFollowing");
      }
      try {
        const response = await withUiDeadline(listCallable(request), {
          phase: "loading Following",
        });
        requireCurrentActor(authInstance, expected);
        return validateListResponse(response?.data, request);
      } catch (error) {
        requireCurrentActor(authInstance, expected);
        throw normalizeServiceError(error);
      }
    },
    async setOwnFollowing(intentValue, { expectedActorUid } = {}) {
      if (!commandsEnabled || !readsEnabled) {
        fail("feature-disabled", "Following commands are not enabled.");
      }
      const request = normalizeSetOwnFollowingIntent(intentValue);
      const expected = authenticatedActor(authInstance, expectedActorUid);
      if (!setCallable) {
        setCallable = callableFactory(functionsInstance, "setOwnFollowing");
      }
      try {
        const response = await withUiDeadline(setCallable(request), {
          phase: "updating Following",
        });
        requireCurrentActor(authInstance, expected);
        return validateResponse(response?.data, request);
      } catch (error) {
        requireCurrentActor(authInstance, expected);
        throw normalizeServiceError(error);
      }
    },
  });
}

export const followingAuthorityService = createFollowingAuthorityService();
